import React, { useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import { getCountryName } from 'src/core/json'
import { showNotification } from 'src/core/helpers'
import { Form, Button, Upload, Divider } from 'antd'
import { useAppDispatch, useAppSelector } from 'src/app/hooks'
import { InboxOutlined, SendOutlined } from '@ant-design/icons'
import { FleetDataType, FleetFormValues } from 'src/core/models'
import { CustomTextInput, CountrySelector } from 'src/components'
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface'
import { createOrEditFleet } from 'src/features/manage-cabify-process/actions'
import { countryListSelector, fleetFormValue, getFleetSelector } from 'src/features'

interface FleetFormProps {
  handleCancel: () => void
  fleet?: FleetDataType
}

const FleetForm: React.FC<FleetFormProps> = ({ handleCancel, fleet }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const dispatch = useAppDispatch()
  const { successCreate, loading: loadingCreate } = useAppSelector(getFleetSelector)
  const formValue = useAppSelector((state) => fleetFormValue(state, fleet?.id))
  const countryList = useAppSelector(countryListSelector)

  const onFinish = (values: FleetFormValues) => {
    const { country, period, file } = values
    const countryState= countryList?.find(c => c.id === country)
    const payload = {
      country: getCountryName(countryState.code2, countryState.name, 'es'),
      period,
      file: file.file
    }
    const fleetId = fleet?.id
    dispatch(createOrEditFleet(payload, fleetId))
  }

  const handleBeforeUpload = (): boolean => {
    showNotification('success', t('admin.MassiveCalculate.FileUploadSuccess'))
    return false
  }

  const handleChange = (info: UploadChangeParam<UploadFile<any>>) => {
    const { status } = info.file
    if (status === 'error') {
      showNotification('error', t('admin.MassiveCalculate.FileUploadError'))
    } else if (status === 'removed') {
      showNotification('success', t('admin.MassiveCalculate.FileUploadRemoved'))
    }
  }

  useEffect(() => {
    if (successCreate) { handleCancel() }
  }, [dispatch, handleCancel, successCreate])

  useEffect(() => {
    if (formValue) {
      form.setFieldsValue({
        country: formValue.country,
        period: formValue.period,
      })
    }
  }, [form, formValue])

  return <>
    <Form name='fleetForm' form={form} onFinish={onFinish} autoComplete='off'>
      <CountrySelector
        name='country'
        placeholder={t('Cabify.Country')}
        required
      />
      <CustomTextInput name='period' placeholder={t('Cabify.Period')} required />
      <Form.Item name='file' rules={[{ required: true, message: t('general.RequiredValidation') }]}>
        <Upload.Dragger
          name='file'
          accept='.xlsx, .xls'
          maxCount={1}
          beforeUpload={handleBeforeUpload}
          onChange={handleChange}
        >
          <p className='ant-upload-drag-icon'>
            <InboxOutlined />
          </p>
          <p className='ant-upload-text'>{t('admin.MassiveCalculate.UploadText')}</p>
          <p className='ant-upload-hint'>{t('admin.MassiveCalculate.FileAllowed')}</p>
        </Upload.Dragger>
      </Form.Item>

      <Divider />
      <Button
        block
        type='primary'
        htmlType='submit'
        className='radius-100'
        loading={loadingCreate}
        icon={<SendOutlined />}
      >
        {loadingCreate ? t('Cabify.CreatingFleet') : t('Cabify.Create')}
      </Button>
    </Form>
  </>
}

export default FleetForm
